// configweb.js
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faXmark,
  faCircleQuestion,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import {
  updateInstanceWebhook,
  getInstanceStatus,
} from "../../../services/api";
import { apiRequest } from "../../../config/api";
import "./configweb.css";

const ConfigWeb = ({ isOpen, onClose, connection }) => {
  const [webhookUrl, setWebhookUrl] = useState("");
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [urlError, setUrlError] = useState("");
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [initialLoading, setInitialLoading] = useState(false);
  const { t } = useTranslation();

  // Webhook padrão que sempre estará ativo e não pode ser alterado
  const DEFAULT_WEBHOOK = {
    url: "https://automacao.wappfy.com.br/webhook/middleware/messages-upsert",
    events: ["message.any"],
  };

  // Lista de eventos disponíveis para seleção (removendo message.any pois é padrão)
  const availableEvents = [
    {
      id: "session.status",
      label: t("webhook.sessionStatus", "Status da Sessão"),
      description: t(
        "webhook.sessionStatusDesc",
        "Mudanças de status da sessão"
      ),
    },
    {
      id: "message",
      label: t("webhook.message", "Mensagem"),
      description: t(
        "webhook.messageDesc",
        "Notificações de novas mensagens recebidas"
      ),
    },
    {
      id: "messages.upsert",
      label: t("webhook.messagesReceived", "Mensagens Recebidas"),
      description: t(
        "webhook.messagesReceivedDesc",
        "Todas as mensagens recebidas"
      ),
    },
    {
      id: "messages.set",
      label: t("webhook.messagesSent", "Mensagem Enviada"),
      description: t(
        "webhook.messagesSentDesc",
        "Mensagens enviadas"
      ),
    },
    {
      id: "message.reaction",
      label: t("webhook.messageReaction", "Reação em Mensagem"),
      description: t("webhook.messageReactionDesc", "Reações em mensagens"),
    },
    {
      id: "message.ack",
      label: t("webhook.messageAck", "Confirmação de Mensagem"),
      description: t(
        "webhook.messageAckDesc",
        "Confirmação de entrega/leitura"
      ),
    },
    {
      id: "message.revoked",
      label: t("webhook.messageRevoked", "Mensagem Removida"),
      description: t("webhook.messageRevokedDesc", "Mensagens apagadas"),
    },
    {
      id: "message.edited",
      label: t("webhook.messageEdited", "Mensagem Editada"),
      description: t("webhook.messageEditedDesc", "Mensagens editadas"),
    },
    {
      id: "group.v2.join",
      label: t("webhook.groupJoin", "Entrou no Grupo"),
      description: t("webhook.groupJoinDesc", "Entradas em grupos"),
    },
    {
      id: "group.v2.leave",
      label: t("webhook.groupLeave", "Saiu do Grupo"),
      description: t("webhook.groupLeaveDesc", "Saídas de grupos"),
    },
    {
      id: "group.v2.participants",
      label: t("webhook.groupParticipants", "Participantes do Grupo"),
      description: t(
        "webhook.groupParticipantsDesc",
        "Mudanças de participantes em grupos"
      ),
    },
    {
      id: "group.v2.update",
      label: t("webhook.groupUpdate", "Grupo Atualizado"),
      description: t("webhook.groupUpdateDesc", "Atualizações em grupos"),
    },
    {
      id: "poll.vote",
      label: t("webhook.pollVote", "Voto em Enquete"),
      description: t("webhook.pollVoteDesc", "Votos em enquetes"),
    },
    {
      id: "event.response",
      label: t("webhook.eventResponse", "Resposta de Evento"),
      description: t("webhook.eventResponseDesc", "Respostas de eventos"),
    },
    {
      id: "event.response.failed",
      label: t("webhook.eventResponseFailed", "Falha na Resposta de Evento"),
      description: t(
        "webhook.eventResponseFailedDesc",
        "Falhas na resposta de eventos"
      ),
    },
    {
      id: "presence.update",
      label: t("webhook.presenceUpdate", "Atualização de Presença"),
      description: t(
        "webhook.presenceUpdateDesc",
        "Mudanças de presença dos contatos"
      ),
    },
  ];

  // Corrige: useEffect deve ficar antes de qualquer return
  React.useEffect(() => {
    const fetchWebhookConfig = async () => {
      if (!isOpen || !connection) return;
      setInitialLoading(true);
      setApiError("");
      try {
        // Busca instanceName e server_id
        const instanceName =
          connection?.instanceName ||
          connection?.instance ||
          connection?.rawData?.instanceName;
        let server_id =
          connection?.server_id ??
          connection?.rawData?.server_id ??
          connection?.rawData?.serverId ??
          connection?.serverId;

        // Busca da API se não encontrar
        if (
          (!server_id ||
            server_id === "undefined" ||
            server_id === "null" ||
            server_id === "") &&
          instanceName
        ) {
          try {
            const dbInstance = await apiRequest(`/instances/${instanceName}`);
            server_id = dbInstance?.server_id;
          } catch {}
        }

        // Garante número
        if (
          server_id !== undefined &&
          server_id !== null &&
          server_id !== "" &&
          !isNaN(Number(server_id))
        ) {
          server_id = Number(server_id);
        }

        if (!instanceName || !server_id || isNaN(server_id)) {
          setInitialLoading(false);
          return;
        }

        // Busca status/configuração da instância
        const statusData = await getInstanceStatus(instanceName, server_id);
        const webhooks = statusData?.config?.webhooks;

        if (Array.isArray(webhooks) && webhooks.length > 0) {
          // Filtra webhooks que não são o padrão
          const customWebhooks = webhooks.filter(
            (webhook) => webhook.url !== DEFAULT_WEBHOOK.url
          );

          if (customWebhooks.length > 0) {
            setWebhookUrl(customWebhooks[0].url || "");
            setSelectedEvents(
              Array.isArray(customWebhooks[0].events)
                ? customWebhooks[0].events
                : []
            );
          } else {
            setWebhookUrl("");
            setSelectedEvents([]);
          }
        } else {
          setWebhookUrl("");
          setSelectedEvents([]);
        }
      } catch (err) {
        setApiError("Erro ao buscar configurações de webhook");
      }
      setInitialLoading(false);
    };

    if (isOpen) {
      fetchWebhookConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, connection]);

  if (!isOpen) return null;

  const validateUrl = (url) => {
    try {
      new URL(url);
      setUrlError("");
      return true;
    } catch {
      setUrlError(
        t(
          "webhook.urlInvalid",
          "Invalid URL. Enter a full URL starting with http:// or https://"
        )
      );
      return false;
    }
  };

  const handleUrlChange = (e) => {
    const url = e.target.value;
    setWebhookUrl(url);
    if (url) validateUrl(url);
    else setUrlError("");
  };

  const handleEventToggle = (eventId) => {
    setSelectedEvents((prev) =>
      prev.includes(eventId)
        ? prev.filter((e) => e !== eventId)
        : [...prev, eventId]
    );
  };

  const handleSave = async () => {
    if (!webhookUrl || !validateUrl(webhookUrl)) {
      return;
    }
    setLoading(true);
    setApiError("");

    // Busca instanceName
    const instanceName =
      connection?.instanceName ||
      connection?.instance ||
      connection?.rawData?.instanceName;
    let server_id =
      connection?.server_id ??
      connection?.rawData?.server_id ??
      connection?.rawData?.serverId ??
      connection?.serverId;

    // Se não encontrou server_id, tenta buscar da API
    if (
      (!server_id ||
        server_id === "undefined" ||
        server_id === "null" ||
        server_id === "") &&
      instanceName
    ) {
      try {
        const dbInstance = await apiRequest(`/instances/${instanceName}`);
        server_id = dbInstance?.server_id;
      } catch (e) {
        // ignora erro, vai cair na validação abaixo
      }
    }

    // Garante que server_id seja número
    if (
      server_id !== undefined &&
      server_id !== null &&
      server_id !== "" &&
      !isNaN(Number(server_id))
    ) {
      server_id = Number(server_id);
    }

    if (!instanceName || !server_id || isNaN(server_id)) {
      setApiError(
        <>
          Esta instância não possui server_id. Não é possível salvar o webhook.
          Contate o suporte.
          <br />
          <pre style={{ fontSize: 10, whiteSpace: "pre-wrap" }}>
            {JSON.stringify(connection, null, 2)}
          </pre>
        </>
      );
      setLoading(false);
      return;
    }

    try {
      // Monta o objeto connection com server_id garantido (número)
      const connectionWithServerId = { ...connection, server_id };

      // Se o webhook estiver desabilitado, envie um array vazio para remover
      await updateInstanceWebhook({
        connection: connectionWithServerId,
        webhookUrl: webhookUrl,
        events: selectedEvents,
      });

      // Atualiza o estado local para refletir o que foi salvo
      setLoading(false);
      onClose();
    } catch (err) {
      setApiError(err.message || "Erro ao salvar webhook");
      setLoading(false);
    }
  };

  return (
    <div
      className="modal-container"
      style={{
        zIndex: 10000,
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
      }}
    >
      <div className="modal-backdrop" onClick={onClose}></div>
      <div
        className="webhook-config-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div className="modal-header-content">
            <div className="header-text">
              <h2 className="modal-title">
                {t("webhook.title", "Configurar Webhook")}
              </h2>
              <div className="connection-info">
                <span className="instance-name">
                  {connection?.instanceName || connection?.instance}
                </span>
                <span className="connection-number">{connection?.number}</span>
              </div>
            </div>
          </div>
          <button
            className="close-button"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <div className="modal-content">
          {initialLoading ? (
            <div
              style={{
                textAlign: "center",
                padding: 32,
                color: "#10b981",
                fontWeight: 600,
              }}
            >
              Carregando configurações...
            </div>
          ) : (
            <>
              {/* Webhook Padrão (Oculto) */}
              <div className="default-webhook-notice">
                <div className="default-webhook-content">
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    className="default-webhook-icon"
                  />
                  <div className="default-webhook-text">
                    <h4>{t("webhook.defaultWebhook.title")}</h4>
                    <p>{t("webhook.defaultWebhook.description")}</p>
                  </div>
                </div>
              </div>

              {/* URL Input */}
              <div className="url-input-container">
                <label className="url-label">
                  {t("webhook.url", "Webhook URL")}
                </label>
                <div className="url-input-wrapper">
                  <input
                    type="url"
                    className={`url-input ${urlError ? "url-input-error" : ""}`}
                    value={webhookUrl}
                    onChange={handleUrlChange}
                    placeholder={t(
                      "webhook.urlPlaceholder",
                      "https://your-url.com/webhook"
                    )}
                  />
                  {urlError && (
                    <div className="url-error-message">
                      <FontAwesomeIcon icon={faInfoCircle} />
                      <span>{urlError}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Events Section */}
              <div className="events-container">
                <label className="events-label">
                  {t("webhook.events", "Events")}
                </label>
                <div className="events-tags">
                  {availableEvents.map((event) => (
                    <div key={event.id} className="event-tag-wrapper">
                      <button
                        onClick={() => handleEventToggle(event.id)}
                        className={`event-tag ${
                          selectedEvents.includes(event.id)
                            ? "event-tag-active"
                            : "event-tag-default"
                        }`}
                      >
                        {event.label}
                      </button>
                      <div className="event-tooltip">{event.description}</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {apiError && (
          <div
            className="api-error-message"
            style={{ color: "red", margin: 8 }}
          >
            {apiError}
          </div>
        )}

        <div
          className="modal-footer"
          style={{ display: "flex", justifyContent: "space-between", gap: 12 }}
        >
          <button
            className="save-button"
            onClick={handleSave}
            disabled={
              !webhookUrl || urlError || loading || selectedEvents.length === 0
            }
            style={{ flex: 1 }}
          >
            {loading
              ? t("instance.saving", "Salvando...")
              : t("instance.saveChanges", "Save Changes")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfigWeb;
